import type { ApprovalInboxEntry, OutstandingApprovalItem } from '@time-tracking/shared';

/**
 * How the triage list is grouped. `team` is offered only to an administrator
 * viewing every team — an approver's own teams are the whole scope, so grouping
 * by them says nothing.
 */
export type GroupBy = 'person' | 'week' | 'team';

/**
 * Can a bulk action take this row?
 *
 * The server already bakes `!isSelf` into `selectable`, so the second clause is
 * belt-and-braces — deliberately kept, because the defect this whole area exists to fix
 * was a segregation-of-duties rule living in exactly one place. What is NOT wanted is
 * five hand-written copies of the conjunction kept in step by comment; one predicate
 * gives both the redundancy and a single place to change it.
 */
export function isBulkApprovable(item: OutstandingApprovalItem): boolean {
  return item.selectable && !item.isSelf;
}

/**
 * The same rule (R22) for the By-week inbox.
 *
 * A second function rather than a second inline filter, and it lives here beside its
 * twin so the two read as one requirement. They cannot share an implementation: the
 * triage row carries `selectable` and `isSelf` already decided by the server, while the
 * week inbox carries a raw `ApprovalInboxEntry` and has to work them out.
 *
 * The By-week sweeps had no self exclusion at all. That was invisible while the rule was
 * enforced only in the browser — the server accepted the row and approved it — and
 * became a visible partial failure the moment the server started refusing with
 * `self_approval`. A guard added on one side turning a silent gap on the other into an
 * error is the shape recorded in
 * docs/solutions/conventions/review-fix-surfaces-dormant-data-2026-09-02.md.
 */
export function isWeekBulkApprovable(
  entry: Pick<ApprovalInboxEntry, 'userId' | 'timesheetId' | 'status'>,
  currentUserId: string | null,
): boolean {
  return (
    entry.status === 'submitted' && entry.timesheetId !== null && entry.userId !== currentUserId
  );
}

/** Stands in for "no team" so a teamless person still forms one group. */
export const UNASSIGNED_GROUP_KEY = '__unassigned__';

export interface ActionGroup {
  /** Identity, never a display string — see `groupKeyFor`. */
  key: string;
  items: OutstandingApprovalItem[];
  /** Worst lateness in the group, driving the urgency rail. */
  worstDaysPastDue: number;
  counts: { approve: number; nudge: number; waiting: number };
  /** Rows the viewer can approve directly, for the group's Approve button. */
  approvableCount: number;
}

/**
 * The grouping key.
 *
 * Always an id. Grouping people by display name would silently merge two
 * colleagues who share one — a `Map.has` collision that drops rows without any
 * error, exactly as
 * docs/solutions/ui-bugs/timesheet-grid-groups-by-non-unique-project-name-2026-05-10.md
 * documents one level down.
 */
export function groupKeyFor(item: OutstandingApprovalItem, groupBy: GroupBy): string {
  if (groupBy === 'person') return item.userId;
  if (groupBy === 'week') return item.weekStartDate;
  return item.teamId ?? UNASSIGNED_GROUP_KEY;
}

/**
 * Group and order the rows.
 *
 * Person and team groups lead with whoever is furthest behind, because the point
 * of the view is triage. Week groups run oldest-first instead: the oldest open
 * week is the one to close out, and chronological order is how a manager reads a
 * backlog of weeks.
 *
 * Within every group, oldest week first.
 *
 * The caller passes rows the server has already filtered, so a group's counts
 * describe exactly what is on screen. A group action must never act on a row the
 * viewer cannot see.
 */
export function groupItems(
  items: readonly OutstandingApprovalItem[],
  groupBy: GroupBy,
): ActionGroup[] {
  const byKey = new Map<string, OutstandingApprovalItem[]>();
  for (const item of items) {
    const key = groupKeyFor(item, groupBy);
    const existing = byKey.get(key);
    if (existing) existing.push(item);
    else byKey.set(key, [item]);
  }

  const groups: ActionGroup[] = [...byKey.entries()].map(([key, groupItemsList]) => {
    const sorted = [...groupItemsList].sort((a, b) =>
      a.weekStartDate.localeCompare(b.weekStartDate),
    );
    return {
      key,
      items: sorted,
      worstDaysPastDue: sorted.reduce((worst, i) => Math.max(worst, i.daysPastDue), 0),
      counts: {
        // Excludes AOBO: those rows offer Review, not Approve, so counting them made
        // the "N to approve" chip disagree with the Approve button beside it.
        approve: sorted.filter((i) => i.kind === 'approve' && !i.aobo).length,
        nudge: sorted.filter((i) => i.kind === 'nudge').length,
        waiting: sorted.filter((i) => i.kind === 'waiting').length,
      },
      // Must match bulkTargets exactly, isSelf included: counting a row the click
      // then drops makes the label overstate, and a group of only the viewer's own
      // sheets renders "Approve 2" that dispatches nothing.
      approvableCount: sorted.filter(isBulkApprovable).length,
    };
  });

  if (groupBy === 'week') {
    return groups.sort((a, b) => a.key.localeCompare(b.key));
  }
  return groups.sort(
    (a, b) =>
      b.worstDaysPastDue - a.worstDaysPastDue ||
      b.items.length - a.items.length ||
      a.key.localeCompare(b.key),
  );
}

export interface GroupDescription {
  /** Heading text. */
  title: string;
  /** The line under it. Materially different per mode — see below. */
  meta: string;
  /** Small chip beside the title, e.g. "no team". */
  tag: string | null;
  /** Avatar initials, only where the group's subject is a person. */
  initials: string | null;
}

/**
 * What a group header says, per grouping mode.
 *
 * This is per-mode on purpose. A single "{n} weeks outstanding" string is not just
 * less informative — under Week and Team grouping it is wrong, because the items in
 * those groups are people, not weeks, so a five-person week group claimed "5 weeks
 * outstanding". Each mode also has a different useful second fact: who someone is and
 * which team they sit in; whether a week is the current one and when it was due; who
 * is actually accountable for a team's backlog.
 */
export function describeGroup(
  group: ActionGroup,
  groupBy: GroupBy,
  formatWeek: (weekStartDate: string) => string,
  formatDueDay: (dueAtIso: string) => string,
  currentUserId: string | null,
): GroupDescription {
  const first = group.items[0];
  const people = new Set(group.items.map((i) => i.userId)).size;
  const plural = (n: number, one: string, many: string): string =>
    `${String(n)} ${n === 1 ? one : many}`;

  if (groupBy === 'person') {
    const where = first?.teamName ?? 'no team';
    return {
      title: first?.name ?? 'Unknown',
      meta: [
        first?.utEid,
        where,
        plural(group.items.length, 'week outstanding', 'weeks outstanding'),
      ]
        .filter((part): part is string => Boolean(part))
        .join(' · '),
      tag: first && first.teamId === null ? 'no team' : null,
      initials: first?.initials ?? null,
    };
  }

  if (groupBy === 'week') {
    const due = first ? formatDueDay(first.dueAt) : null;
    return {
      title: formatWeek(group.key),
      meta: [
        first?.isCurrentWeek === true ? 'Current week' : null,
        due === null ? null : first?.isCurrentWeek === true ? `due ${due}` : `was due ${due}`,
        plural(people, 'person', 'people'),
      ]
        .filter((part): part is string => part !== null)
        .join(' · '),
      tag: null,
      initials: null,
    };
  }

  // Team. "Nobody is chasing these" is the case worth surfacing: a team with no lead
  // has no approver of record, so its backlog belongs to no one until someone acts.
  const isUnassignedGroup = group.key === UNASSIGNED_GROUP_KEY;
  const leadIsViewer = currentUserId !== null && first?.teamLeadUserId === currentUserId;
  const lead = first?.teamLeadDisplayName ?? null;
  return {
    title: isUnassignedGroup ? 'Unassigned' : (first?.teamName ?? 'Unknown team'),
    meta: [
      isUnassignedGroup || lead === null
        ? 'No approver of record · nobody is chasing these'
        : leadIsViewer
          ? 'You are the lead'
          : `Lead: ${lead}`,
      plural(group.items.length, 'open item', 'open items'),
    ].join(' · '),
    tag: null,
    initials: null,
  };
}

/** Severity of a group's urgency rail. Never the only carrier of the state. */
export function groupSeverity(worstDaysPastDue: number): 'none' | 'warn' | 'alert' {
  if (worstDaysPastDue >= 14) return 'alert';
  if (worstDaysPastDue >= 7) return 'warn';
  return 'none';
}

/**
 * Rows one bulk action would act on.
 *
 * Derived from the rows actually returned, never from the uncapped counts: the
 * server caps the list, so a button labelled from the total would approve fewer
 * sheets than it claimed. The approver's own sheet is excluded from every bulk
 * path — it stays individually approvable, but no one-click action should ever
 * self-approve.
 */
export function bulkTargets(
  items: readonly OutstandingApprovalItem[],
  opts: { cleanOnly: boolean },
): OutstandingApprovalItem[] {
  return items.filter((i) => isBulkApprovable(i) && (!opts.cleanOnly || i.anomalies.length === 0));
}
